import React, { useEffect, useRef, useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    IconButton, 
    CircularProgress, 
    Box,
    Typography,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { renderAsync } from 'docx-preview';

const PreviewWord = ({ open, fileItem, onClose }) => {
    const containerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [renderError, setRenderError] = useState(null);

    useEffect(() => {
        const renderWordDoc = async () => {
            if (!open || !fileItem || !containerRef.current) return;

            setLoading(true);
            setRenderError(null);
            
            try {
                let data;
                if (fileItem.blob) {
                    data = fileItem.blob;
                } else if (fileItem.file instanceof File) {
                    data = fileItem.file;
                } else if (fileItem.url) {
                    const response = await fetch(fileItem.url);
                    if (!response.ok) throw new Error("Network response was not ok");
                    data = await response.arrayBuffer();
                }

                if (!data) throw new Error("No file data found.");

                containerRef.current.innerHTML = '';
                
                await renderAsync(data, containerRef.current, null, {
                    className: 'docx-viewer',
                    inWrapper: true,
                });
            } catch (err) {
                setRenderError(err.message);
            } finally {
                setLoading(false);
            }
        };

        const timer = setTimeout(renderWordDoc, 100);
        return () => clearTimeout(timer);
    }, [open, fileItem]);

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="lg" 
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper', py: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                    {fileItem?.name || "Náhľad Word dokumentu"}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'action.hover', color: 'text.secondary' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            
            <Divider />

            <DialogContent dividers sx={{ bgcolor: 'background.default', p: 0 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                        <CircularProgress />
                    </Box>
                )}

                {renderError && (
                    <Box sx={{ p: 4, textAlign: 'center', height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="error" variant="body1">Chyba pri načítaní: {renderError}</Typography>
                    </Box>
                )}
                
                <Box 
                    ref={containerRef} 
                    sx={{ 
                        height: '80vh', 
                        overflow: 'auto',
                        display: loading || renderError ? 'none' : 'block',
                        p: { xs: 2, md: 4 },
                        '& .docx-viewer': {
                            margin: '0 auto',
                            backgroundColor: '#fff', // Document background remains white
                            boxShadow: 3,
                            borderRadius: 1,
                            overflow: 'hidden',
                            color: '#000' // Force text color to black for readability
                        }
                    }} 
                />
            </DialogContent>
        </Dialog>
    );
};

export default PreviewWord;